import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Target, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATS = ["Academic", "Tennis", "Writing", "Personal"];
const CAT_COLOR = { Academic: "bg-indigo-500", Tennis: "bg-emerald-500", Writing: "bg-amber-500", Personal: "bg-purple-500" };

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [cat, setCat] = useState("Academic");

  const load = () => api.get("/goals").then((r) => setGoals(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await api.post("/goals", { title, description: desc, category: cat, progress: 0 });
    setTitle(""); setDesc(""); setCat("Academic");
    toast.success("Goal added");
    load();
  };

  const setProgress = async (g, delta) => {
    const p = Math.max(0, Math.min(100, g.progress + delta));
    await api.put(`/goals/${g.id}`, { progress: p });
    load();
  };

  const remove = async (id) => { await api.delete(`/goals/${id}`); toast.success("Goal removed"); load(); };

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="goal-form">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input data-testid="goal-title-input" placeholder="Goal title (e.g. Score 7 in Math)" value={title} onChange={(e) => setTitle(e.target.value)} className="sm:col-span-2 rounded-xl" />
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="goal-cat-select" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Textarea data-testid="goal-desc-input" placeholder="Why does this matter? (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-xl resize-none" rows={2} />
        <Button data-testid="goal-add-btn" type="submit" className="rounded-xl bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" /> Add goal</Button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="goals-grid">
        {goals.length === 0 && <p className="text-sm text-slate-400">No goals yet. What does Ayaan want to achieve this term?</p>}
        {goals.map((g, i) => (
          <motion.div key={g.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${CAT_COLOR[g.category]}`} />
                <span className="text-xs font-semibold text-slate-500">{g.category}</span>
              </div>
              <button data-testid={`goal-delete-${g.id}`} onClick={() => remove(g.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <h3 className="font-heading font-semibold text-slate-900 mt-2 flex items-center gap-2"><Target className="w-4 h-4 text-indigo-500" />{g.title}</h3>
            {g.description && <p className="text-sm text-slate-500 mt-1">{g.description}</p>}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-slate-500">Progress</span>
                <span className="text-sm font-bold text-indigo-600">{g.progress}%</span>
              </div>
              <Progress value={g.progress} className="h-2" />
              <div className="flex gap-2 mt-3">
                <Button data-testid={`goal-minus-${g.id}`} onClick={() => setProgress(g, -10)} variant="outline" size="sm" className="rounded-lg flex-1"><Minus className="w-3.5 h-3.5" /></Button>
                <Button data-testid={`goal-plus-${g.id}`} onClick={() => setProgress(g, 10)} size="sm" className="rounded-lg flex-1 bg-indigo-600 hover:bg-indigo-700"><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
